// This component is a thin portal wrapper around the shared POD product preview module.

// Use React tools we need for stable preview rendering.
import { useMemo } from "react";

// Reuse the shared external-script preview hook.
import { usePodProductPreview } from "global/hooks/usePodProductPreview";

// Keep role transform payloads compatible with modules that expect either UK or US spelling.
const normaliseRoleTransformData = (roleData) => {

    // Exit early when there is no role data to normalise.
    if (!roleData || typeof roleData !== "object") {
        return roleData;
    }

    // Resolve centre values from either supported key spelling.
    const resolvedCentreX = roleData?.centre_x ?? roleData?.center_x;
    const resolvedCentreY = roleData?.centre_y ?? roleData?.center_y;

    // Return the original payload plus both aliases so shared preview code can read either form.
    return {
        ...roleData,
        centre_x: resolvedCentreX,
        center_x: resolvedCentreX,
        centre_y: resolvedCentreY,
        center_y: resolvedCentreY,
    };

};

// Render POD previews through the shared standalone module while keeping the existing portal props stable.
export function PodProductPreview({
    thedesign,
    product,
    child,
    currentProductRoleData = null,
    currentProductChildRoleData = null,
    mockup_height = 100,
    options = {},
}) {

    // Only load the shared module when there is enough raw data to build a preview.
    const shouldLoadPreview =
        Boolean(thedesign?.role || thedesign?.roleItem?.role) &&
        Boolean(child || product);

    const {
        renderProductPreview,
        isReady,
    } = usePodProductPreview({
        enabled: shouldLoadPreview,
    });

    // Pass the portal's raw preview payload through to the shared module.
    const previewPayload = useMemo(() => ({
        thedesign,
        product,
        child,
        currentProductRoleData: normaliseRoleTransformData(currentProductRoleData),
        currentProductChildRoleData: normaliseRoleTransformData(currentProductChildRoleData),
        mockup_height,
        options,
    }), [
        child,
        currentProductChildRoleData,
        currentProductRoleData,
        mockup_height,
        options,
        product,
        thedesign,
    ]);

    // Ask the shared module to build the preview markup once the script is ready.
    const previewHtml = useMemo(() => {

        // Stop until the shared preview module is actually ready.
        if (!shouldLoadPreview || !isReady) {
            return "";
        }

        try {

            // Delegate the full preview normalization and rendering to the shared module.
            return renderProductPreview(previewPayload);

        } catch (error) {

            // Keep the UI stable and let the console expose the underlying issue during migration.
            console.error("Failed to render POD product preview.", error);
            return "";

        }

    }, [isReady, previewPayload, renderProductPreview, shouldLoadPreview]);

    // Match the current preview behavior by rendering nothing when the preview cannot be built.
    if (!previewHtml) {
        return null;
    }

    // Render the shared module's HTML output directly.
    return <div dangerouslySetInnerHTML={{ __html: previewHtml }} />;

}
